// @ts-check

import { useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  getOrgConfigForTenant as getOrgConfigForTenantQuery,
  updateOrgConfigForTenant as updateOrgConfigForTenantMutation,
} from "src/queries/orgConfig.graphql";
import { omitTypename } from "src/util/graphql";
import { getGraphQLErrors } from "src/util/error";

/**
 * Hook for managing tenant AI customisations config.
 * Unlike the base useOrgConfigForTenant hook, this throws on mutation errors
 * so callers can display guardrail validation messages to the user.
 */
export const useAiCustomisationsForTenant = () => {
  const { data, loading } = useQuery(getOrgConfigForTenantQuery, {
    fetchPolicy: "cache-first",
  });

  const [updateConfig, { loading: updating }] = useMutation(
    updateOrgConfigForTenantMutation
  );

  const config = useMemo(() => omitTypename(data?.config), [data]);

  const updateAiCustomisations = useCallback(
    /**
     * @param {org_config.tenant.AiCustomisations} newAiCustomisations
     * @returns {Promise<void>}
     * @throws {Error} if validation fails (e.g. guardrail rejection)
     */
    async (newAiCustomisations) => {
      try {
        await updateConfig({
          variables: {
            input: { config: { aiCustomisations: newAiCustomisations } },
          },
          update: (cache, { data: updateData }) => {
            const d = /** @type {any} */ (updateData);
            if (!d?.config) {
              return;
            }
            const existingData = cache.readQuery({
              query: getOrgConfigForTenantQuery,
            });
            if (existingData) {
              cache.writeQuery({
                query: getOrgConfigForTenantQuery,
                data: { config: d.config },
              });
            }
          },
        });
      } catch (error) {
        const gqlErrors = getGraphQLErrors(error);
        if (gqlErrors.length) {
          const err = new Error(gqlErrors[0]?.message || error.message);
          // @ts-expect-error - attaching graphQLErrors for error handling
          err.graphQLErrors = gqlErrors;
          throw err;
        }
        throw error;
      }
    },
    [updateConfig]
  );

  return {
    loading,
    aiCustomisations: config?.config?.aiCustomisations,
    update: updateAiCustomisations,
    updating,
  };
};
